import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Box, Typography, Chip, Button, CircularProgress, alpha } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';

import { BBSelect, Flex } from '../';
import AIEditsTabTable from './AIEditsTabTable';
import {
  THEME_COLORS,
  EDIT_TYPES,
  STATUS_TYPES,
  SORT_FIELDS,
  CHIP_STYLES,
  getSectionNames,
  getEditType,
} from './AIEditsTab.utils';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AIEditsTab = ({
  similarSentences = [],
  onApproveSentence = (f) => f,
  onDisapproveSentence = (f) => f,
  onUndoDecision = (f) => f,
  isLoading = false,
  isLoadingMore = false,
}) => {
  const [processingIds, setProcessingIds] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortBy, setSortBy] = useState(SORT_FIELDS.SIMILARITY);
  const [sortOrder, setSortOrder] = useState('desc');
  const [editTypeFilter, setEditTypeFilter] = useState([EDIT_TYPES.ALL]);
  const [statusFilter, setStatusFilter] = useState([STATUS_TYPES.ALL]);
  const [sectionFilter, setSectionFilter] = useState(/** @type {string[]} */ ([]));
  const [manualActions, setManualActions] = useState(new Map());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // Extract unique sections from all sentences
  const availableSections = useMemo(() => {
    if (!similarSentences || similarSentences.length === 0) return [];

    const sectionsSet = new Set();
    similarSentences.forEach((sentence) => {
      if (Array.isArray(sentence.sections)) {
        sentence.sections.forEach((s) => {
          const sectionName = typeof s === 'string' ? s : s.name || s.id || 'Unknown';
          sectionsSet.add(sectionName);
        });
      } else if (sentence.section) {
        sectionsSet.add(sentence.section);
      }
    });

    return Array.from(sectionsSet).sort();
  }, [similarSentences]);

  // Filter and sort similar sentences based on selected criteria
  const filteredAndSortedSentences = useMemo(() => {
    if (!similarSentences || similarSentences.length === 0) return [];

    // First, filter by edit type
    let filtered = similarSentences;
    if (!editTypeFilter.includes(EDIT_TYPES.ALL) && editTypeFilter.length > 0) {
      filtered = similarSentences.filter((sentence) => {
        return editTypeFilter.includes(getEditType(sentence));
      });
    }

    // Then, filter by status
    if (!statusFilter.includes(STATUS_TYPES.ALL) && statusFilter.length > 0) {
      filtered = filtered.filter((sentence) => {
        const status = sentence.status || STATUS_TYPES.PENDING;
        return statusFilter.includes(status);
      });
    }

    // Then, filter by section
    if (sectionFilter.length > 0) {
      filtered = filtered.filter((sentence) => {
        const sectionNames = getSectionNames(sentence);
        return sectionNames.some((name) => sectionFilter.indexOf(name) !== -1);
      });
    }

    // Then, sort the filtered results
    return [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case SORT_FIELDS.SIMILARITY:
          aValue = a.score || 0;
          bValue = b.score || 0;
          break;
        case SORT_FIELDS.STATUS: {
          // Status priority: pending > approved > disapproved
          const statusPriority = {
            [STATUS_TYPES.PENDING]: 0,
            [STATUS_TYPES.APPROVED]: 1,
            [STATUS_TYPES.DISAPPROVED]: 2,
          };
          aValue = statusPriority[a.status || STATUS_TYPES.PENDING];
          bValue = statusPriority[b.status || STATUS_TYPES.PENDING];
          break;
        }
        case SORT_FIELDS.SECTION:
          aValue = getSectionNames(a).join(', ');
          bValue = getSectionNames(b).join(', ');
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [similarSentences, sortBy, sortOrder, editTypeFilter, statusFilter, sectionFilter]);

  const handleSort = useCallback(
    (field) => {
      if (sortBy === field) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(field);
        setSortOrder('desc');
      }
    },
    [sortBy, sortOrder]
  );

  const toggleRowExpanded = useCallback((tuid) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tuid)) {
        newSet.delete(tuid);
      } else {
        newSet.add(tuid);
      }
      return newSet;
    });
  }, []);

  const handleApprove = useCallback(
    async (sentence) => {
      // Prevent duplicate processing or processing during bulk operations
      if (processingIds.has(sentence.tuid) || isBulkProcessing) {
        return;
      }

      setProcessingIds((prev) => new Set([...prev, sentence.tuid]));
      setManualActions((prev) => new Map(prev).set(sentence.tuid, 'approved'));

      try {
        await onApproveSentence(sentence);
      } catch (error) {
        // Rollback manual action on error
        setManualActions((prev) => {
          const newMap = new Map(prev);
          newMap.delete(sentence.tuid);
          return newMap;
        });
        console.error('Failed to approve sentence:', error);
        // TODO: Show user-facing error notification
      } finally {
        setProcessingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sentence.tuid);
          return newSet;
        });
      }
    },
    [onApproveSentence, processingIds, isBulkProcessing]
  );

  const handleDisapprove = useCallback(
    async (sentence) => {
      // Prevent duplicate processing or processing during bulk operations
      if (processingIds.has(sentence.tuid) || isBulkProcessing) {
        return;
      }

      setProcessingIds((prev) => new Set([...prev, sentence.tuid]));
      setManualActions((prev) => new Map(prev).set(sentence.tuid, 'disapproved'));

      try {
        await onDisapproveSentence(sentence);
      } catch (error) {
        // Rollback manual action on error
        setManualActions((prev) => {
          const newMap = new Map(prev);
          newMap.delete(sentence.tuid);
          return newMap;
        });
        console.error('Failed to disapprove sentence:', error);
        // TODO: Show user-facing error notification
      } finally {
        setProcessingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sentence.tuid);
          return newSet;
        });
      }
    },
    [onDisapproveSentence, processingIds, isBulkProcessing]
  );

  const handleUndo = useCallback(
    async (sentence) => {
      // Prevent duplicate processing or processing during bulk operations
      if (processingIds.has(sentence.tuid) || isBulkProcessing) {
        return;
      }

      const previousAction = manualActions.get(sentence.tuid);

      setProcessingIds((prev) => new Set([...prev, sentence.tuid]));
      setManualActions((prev) => {
        const newMap = new Map(prev);
        newMap.delete(sentence.tuid);
        return newMap;
      });

      try {
        await onUndoDecision(sentence);
      } catch (error) {
        // Restore previous action on error
        if (previousAction) {
          setManualActions((prev) => new Map(prev).set(sentence.tuid, previousAction));
        }
        console.error('Failed to undo decision:', error);
        // TODO: Show user-facing error notification
      } finally {
        setProcessingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sentence.tuid);
          return newSet;
        });
      }
    },
    [onUndoDecision, processingIds, manualActions, isBulkProcessing]
  );

  /**
   * Retry helper for failed API calls with exponential backoff
   * @param {Function} fn - Async function to retry
   * @param {number} maxRetries - Maximum retry attempts
   * @returns {Promise} - Result of the function or final error
   */
  const retryWithBackoff = useCallback(async (fn, maxRetries = 2) => {
    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        // Only retry on server errors (5xx), not client errors (4xx)
        const isServerError =
          error?.response?.status >= 500 && error?.response?.status < 600;
        if (attempt < maxRetries && isServerError) {
          // Exponential backoff: 500ms, 1000ms, 2000ms...
          const delay = 500 * Math.pow(2, attempt);
          console.warn(
            `Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms delay`
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else {
          break; // Don't retry on client errors or after max retries
        }
      }
    }
    throw lastError;
  }, []);

  // Accept All - only sentences that haven't been manually rejected
  const handleAcceptAll = useCallback(async () => {
    // Prevent concurrent bulk operations
    if (isBulkProcessing) {
      console.warn('Bulk operation already in progress');
      return;
    }

    const sentencesToApprove = filteredAndSortedSentences.filter(
      (sentence) =>
        sentence.status === STATUS_TYPES.PENDING &&
        manualActions.get(sentence.tuid) !== STATUS_TYPES.DISAPPROVED
    );

    if (sentencesToApprove.length === 0) {
      return;
    }

    // Set bulk processing flag
    setIsBulkProcessing(true);

    // Mark all as processing
    const tuidsToProcess = sentencesToApprove.map((s) => s.tuid);
    setProcessingIds((prev) => new Set([...prev, ...tuidsToProcess]));

    let successCount = 0;
    let failureCount = 0;
    const failedSentences = [];

    try {
      // Process in batches to avoid overwhelming the server
      const BATCH_SIZE = 10;
      for (let i = 0; i < sentencesToApprove.length; i += BATCH_SIZE) {
        const batch = sentencesToApprove.slice(i, i + BATCH_SIZE);

        // Pre-set manual actions for the batch
        batch.forEach((sentence) => {
          setManualActions((prev) => new Map(prev).set(sentence.tuid, 'approved'));
        });

        const results = await Promise.allSettled(
          batch.map((sentence) => retryWithBackoff(() => onApproveSentence(sentence)))
        );

        results.forEach((result, index) => {
          const sentence = batch[index];
          if (result.status === 'rejected') {
            failureCount++;
            failedSentences.push({ tuid: sentence.tuid, error: result.reason });
            // Rollback manual action on failure
            setManualActions((prev) => {
              const newMap = new Map(prev);
              newMap.delete(sentence.tuid);
              return newMap;
            });
            console.error(`Failed to approve sentence ${sentence.tuid}:`, result.reason);
          } else {
            successCount++;
          }
        });

        // Small delay between batches to prevent rate limiting
        if (i + BATCH_SIZE < sentencesToApprove.length) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }

      // Log summary
      console.log(
        `Bulk approve completed: ${successCount} succeeded, ${failureCount} failed`
      );

      // TODO: Show user-facing success/error notification
      // Example: showNotification({
      //   type: failureCount > 0 ? 'warning' : 'success',
      //   message: `${successCount} sentence${successCount !== 1 ? 's' : ''} approved${
      //     failureCount > 0 ? `, ${failureCount} failed` : ''
      //   }`
      // });
    } catch (error) {
      // Handle unexpected errors during bulk operation
      console.error('Bulk approve operation failed:', error);

      // Rollback all manual actions
      tuidsToProcess.forEach((tuid) => {
        setManualActions((prev) => {
          const newMap = new Map(prev);
          newMap.delete(tuid);
          return newMap;
        });
      });

      // TODO: Show user-facing error notification
      // Example: showNotification({
      //   type: 'error',
      //   message: 'Bulk approve operation failed. Please try again.'
      // });
    } finally {
      // Always clear processing state and bulk flag
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        tuidsToProcess.forEach((tuid) => newSet.delete(tuid));
        return newSet;
      });
      setIsBulkProcessing(false);
    }
  }, [filteredAndSortedSentences, manualActions, onApproveSentence, isBulkProcessing]);

  // Reject All - only sentences that haven't been manually accepted
  const handleRejectAll = useCallback(async () => {
    // Prevent concurrent bulk operations
    if (isBulkProcessing) {
      console.warn('Bulk operation already in progress');
      return;
    }

    const sentencesToReject = filteredAndSortedSentences.filter(
      (sentence) =>
        sentence.status === STATUS_TYPES.PENDING &&
        manualActions.get(sentence.tuid) !== STATUS_TYPES.APPROVED
    );

    if (sentencesToReject.length === 0) {
      return;
    }

    // Set bulk processing flag
    setIsBulkProcessing(true);

    // Mark all as processing
    const tuidsToProcess = sentencesToReject.map((s) => s.tuid);
    setProcessingIds((prev) => new Set([...prev, ...tuidsToProcess]));

    let successCount = 0;
    let failureCount = 0;
    const failedSentences = [];

    try {
      // Process in batches to avoid overwhelming the server
      const BATCH_SIZE = 10;
      for (let i = 0; i < sentencesToReject.length; i += BATCH_SIZE) {
        const batch = sentencesToReject.slice(i, i + BATCH_SIZE);

        // Pre-set manual actions for the batch
        batch.forEach((sentence) => {
          setManualActions((prev) => new Map(prev).set(sentence.tuid, 'disapproved'));
        });

        const results = await Promise.allSettled(
          batch.map((sentence) => retryWithBackoff(() => onDisapproveSentence(sentence)))
        );

        results.forEach((result, index) => {
          const sentence = batch[index];
          if (result.status === 'rejected') {
            failureCount++;
            failedSentences.push({ tuid: sentence.tuid, error: result.reason });
            // Rollback manual action on failure
            setManualActions((prev) => {
              const newMap = new Map(prev);
              newMap.delete(sentence.tuid);
              return newMap;
            });
            console.error(
              `Failed to disapprove sentence ${sentence.tuid}:`,
              result.reason
            );
          } else {
            successCount++;
          }
        });

        // Small delay between batches to prevent rate limiting
        if (i + BATCH_SIZE < sentencesToReject.length) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }

      // Log summary
      console.log(
        `Bulk reject completed: ${successCount} succeeded, ${failureCount} failed`
      );

      // TODO: Show user-facing success/error notification
      // Example: showNotification({
      //   type: failureCount > 0 ? 'warning' : 'success',
      //   message: `${successCount} sentence${successCount !== 1 ? 's' : ''} rejected${
      //     failureCount > 0 ? `, ${failureCount} failed` : ''
      //   }`
      // });
    } catch (error) {
      // Handle unexpected errors during bulk operation
      console.error('Bulk reject operation failed:', error);

      // Rollback all manual actions
      tuidsToProcess.forEach((tuid) => {
        setManualActions((prev) => {
          const newMap = new Map(prev);
          newMap.delete(tuid);
          return newMap;
        });
      });

      // TODO: Show user-facing error notification
      // Example: showNotification({
      //   type: 'error',
      //   message: 'Bulk reject operation failed. Please try again.'
      // });
    } finally {
      // Always clear processing state and bulk flag
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        tuidsToProcess.forEach((tuid) => newSet.delete(tuid));
        return newSet;
      });
      setIsBulkProcessing(false);
    }
  }, [filteredAndSortedSentences, manualActions, onDisapproveSentence, isBulkProcessing]);

  // Count pending sentences for bulk actions
  const pendingCount = useMemo(() => {
    return filteredAndSortedSentences.filter((s) => s.status === STATUS_TYPES.PENDING)
      .length;
  }, [filteredAndSortedSentences]);

  const canAcceptAll = useMemo(() => {
    // Can accept if there are any pending sentences (regardless of manual actions)
    // Manual actions only affect what gets processed, not button availability
    return filteredAndSortedSentences.some((s) => s.status === STATUS_TYPES.PENDING);
  }, [filteredAndSortedSentences]);

  const canRejectAll = useMemo(() => {
    // Can reject if there are any pending sentences (regardless of manual actions)
    // Manual actions only affect what gets processed, not button availability
    return filteredAndSortedSentences.some((s) => s.status === STATUS_TYPES.PENDING);
  }, [filteredAndSortedSentences]);

  // Memoized filter handlers
  const handleEditTypeToggle = useCallback((type) => {
    if (type === EDIT_TYPES.ALL) {
      setEditTypeFilter([EDIT_TYPES.ALL]);
    } else {
      setEditTypeFilter((prev) => {
        const isSelected = prev.includes(type);
        if (isSelected) {
          const newFilter = prev.filter((t) => t !== type);
          return newFilter.length === 0 ? [EDIT_TYPES.ALL] : newFilter;
        } else {
          return [...prev.filter((t) => t !== EDIT_TYPES.ALL), type];
        }
      });
    }
  }, []);

  const handleStatusToggle = useCallback((status) => {
    if (status === STATUS_TYPES.ALL) {
      setStatusFilter([STATUS_TYPES.ALL]);
    } else {
      setStatusFilter((prev) => {
        const isSelected = prev.includes(status);
        if (isSelected) {
          const newFilter = prev.filter((s) => s !== status);
          return newFilter.length === 0 ? [STATUS_TYPES.ALL] : newFilter;
        } else {
          return [...prev.filter((s) => s !== STATUS_TYPES.ALL), status];
        }
      });
    }
  }, []);

  if (isLoading && similarSentences.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Finding similar sentences...</Typography>
      </Box>
    );
  }

  // Show loading indicator when no results yet but searches are in progress
  if (similarSentences.length === 0 && isLoadingMore) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        sx={{
          bgcolor: THEME_COLORS.background.page,
          borderRadius: 2,
          p: 4,
        }}
      >
        <CircularProgress size={48} thickness={4} />
        <Typography
          variant="h6"
          sx={{
            mt: 3,
            fontWeight: 600,
            color: THEME_COLORS.text.primary,
          }}
        >
          Searching for similar sentences...
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            color: THEME_COLORS.text.tertiary,
          }}
        >
          This may take a few moments
        </Typography>
      </Box>
    );
  }

  if (similarSentences.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="textSecondary">
          No similar sentences found
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Try adjusting your search criteria or similarity threshold
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: THEME_COLORS.background.page,
        borderRadius: 2,
        p: 3,
      }}
      role="region"
      aria-label="AI Suggested Edits Review"
    >
      {/* Header Section */}
      <Box
        sx={{
          mb: 4,
          pb: 3,
          borderBottom: '2px solid',
          borderColor: THEME_COLORS.border.default,
        }}
        role="banner"
      >
        <Flex.H sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: THEME_COLORS.text.secondary,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 1,
                display: 'block',
              }}
            >
              AI Suggested Edits
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: THEME_COLORS.text.tertiary,
                mt: 0.5,
              }}
            >
              {filteredAndSortedSentences.length} sentence
              {filteredAndSortedSentences.length !== 1 ? 's' : ''} found
              {filteredAndSortedSentences.length !== similarSentences.length &&
                ` (filtered from ${similarSentences.length} total)`}
            </Typography>
          </Box>
          {isLoadingMore && (
            <Flex.H sx={{ alignItems: 'center', gap: '8px' }}>
              <CircularProgress size={18} thickness={4} />
              <Typography
                variant="body2"
                sx={{
                  color: THEME_COLORS.text.tertiary,
                  fontSize: '0.875rem',
                }}
              >
                Loading more...
              </Typography>
            </Flex.H>
          )}
        </Flex.H>
      </Box>
      {/* Filters Section */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          bgcolor: THEME_COLORS.background.card,
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
        role="search"
        aria-label="Filter AI edits"
      >
        <Flex.H sx={{ alignItems: 'center', gap: '8px', mb: 2 }}>
          <FilterListIcon sx={{ fontSize: 20, color: THEME_COLORS.text.tertiary }} />

          <Typography
            variant="body2"
            sx={{
              color: THEME_COLORS.text.secondary,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'block',
            }}
          >
            Filters
          </Typography>
        </Flex.H>

        <Flex.H sx={{ justifyContent: 'space-between', gap: '16px', mb: 2 }}>
          {/* Edit Type Filter */}
          <Box role="group" aria-labelledby="edit-type-label">
            <Typography
              id="edit-type-label"
              variant="h4"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                mb: 0.75,
              }}
            >
              Edit Type
            </Typography>

            <Flex.H sx={{ gap: '8px' }}>
              {[
                EDIT_TYPES.ALL,
                EDIT_TYPES.ACCEPT,
                EDIT_TYPES.REJECT,
                EDIT_TYPES.REVISE,
              ].map((type) => {
                const isSelected = editTypeFilter.includes(type);
                const label = type.charAt(0).toUpperCase() + type.slice(1);

                return (
                  <Chip
                    key={type}
                    label={label}
                    onClick={() => handleEditTypeToggle(type)}
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleEditTypeToggle(type);
                      }
                    }}
                    sx={{
                      ...CHIP_STYLES.base,
                      ...(isSelected && CHIP_STYLES.selected),
                    }}
                  />
                );
              })}
            </Flex.H>
          </Box>

          {/* Status Filter */}
          <Box role="group" aria-labelledby="status-label">
            <Typography
              id="status-label"
              variant="h4"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                mb: 0.75,
              }}
            >
              Status
            </Typography>
            <Flex.H sx={{ gap: '8px' }}>
              {[
                STATUS_TYPES.ALL,
                STATUS_TYPES.PENDING,
                STATUS_TYPES.APPROVED,
                STATUS_TYPES.DISAPPROVED,
              ].map((status) => {
                const isSelected = statusFilter.includes(status);
                const label = status.charAt(0).toUpperCase() + status.slice(1);

                return (
                  <Chip
                    key={status}
                    label={label}
                    onClick={() => handleStatusToggle(status)}
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleStatusToggle(status);
                      }
                    }}
                    sx={{
                      ...CHIP_STYLES.base,
                      ...(isSelected && CHIP_STYLES.selected),
                    }}
                  />
                );
              })}
            </Flex.H>
          </Box>
        </Flex.H>

        {/* Section Filter */}
        {availableSections.length > 0 && (
          <Box role="group" aria-labelledby="section-label">
            <Typography
              id="section-label"
              variant="h4"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                mb: 0.75,
              }}
            >
              Sections
            </Typography>

            <BBSelect
              isMulti
              placeholder="All Sections"
              options={availableSections.map((section) => ({
                value: section,
                label: section,
              }))}
              value={sectionFilter}
              onChange={(selected) => {
                setSectionFilter(selected?.map((s) => s.value) || []);
              }}
              isFullWidth
              isLoading={false}
              isDisabled={false}
              sx={{
                '& .react-select__control': {
                  borderRadius: '8px',
                },
              }}
            />
          </Box>
        )}
      </Box>
      {/* Bulk Actions */}
      {pendingCount > 0 && (
        <Flex.H
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            mb: 3,
            p: 2,
            bgcolor: alpha(THEME_COLORS.primary, 0.05),
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: alpha(THEME_COLORS.primary, 0.1),
          }}
          role="group"
          aria-label="Bulk actions"
        >
          <Typography
            variant="body1"
            sx={{
              color: THEME_COLORS.text.secondary,
              fontWeight: 600,
            }}
          >
            {pendingCount} pending sentence{pendingCount !== 1 ? 's' : ''}
          </Typography>

          <Flex.H sx={{ gap: '16px' }}>
            <Button
              variant="contained"
              startIcon={
                isBulkProcessing ? (
                  <CircularProgress size={16} sx={{ color: 'inherit' }} />
                ) : (
                  <CheckCircleOutlineIcon />
                )
              }
              onClick={handleAcceptAll}
              disabled={!canAcceptAll || isBulkProcessing}
              aria-label={`Accept all ${pendingCount} pending sentences`}
              aria-busy={isBulkProcessing}
              sx={{
                bgcolor: THEME_COLORS.success,
                color: THEME_COLORS.background.card,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#0F6B50',
                  boxShadow: `0 4px 12px ${alpha(THEME_COLORS.success, 0.3)}`,
                },
                '&:disabled': {
                  bgcolor: '#E2E8F0',
                  color: THEME_COLORS.text.quaternary,
                },
              }}
            >
              {isBulkProcessing ? 'Processing...' : 'Accept All Pending'}
            </Button>
            <Button
              variant="contained"
              startIcon={
                isBulkProcessing ? (
                  <CircularProgress size={16} sx={{ color: 'inherit' }} />
                ) : (
                  <CancelOutlinedIcon />
                )
              }
              onClick={handleRejectAll}
              disabled={!canRejectAll || isBulkProcessing}
              aria-label={`Reject all ${pendingCount} pending sentences`}
              aria-busy={isBulkProcessing}
              sx={{
                bgcolor: THEME_COLORS.error,
                color: THEME_COLORS.background.card,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#850A0D',
                  boxShadow: `0 4px 12px ${alpha(THEME_COLORS.error, 0.3)}`,
                },
                '&:disabled': {
                  bgcolor: '#E2E8F0',
                  color: THEME_COLORS.text.quaternary,
                },
              }}
            >
              {isBulkProcessing ? 'Processing...' : 'Reject All Pending'}
            </Button>
          </Flex.H>
        </Flex.H>
      )}{' '}
      {/* Description */}
      <Typography
        variant="body1"
        sx={{
          color: THEME_COLORS.text.tertiary,
          mb: 3,
        }}
      >
        Review similar sentences and approve those that should be included in your rule.
        Use filters to narrow down by edit type, approval status, or contract section.
      </Typography>
      {/* Table Section */}
      <AIEditsTabTable
        filteredAndSortedSentences={filteredAndSortedSentences}
        sortBy={sortBy}
        sortOrder={sortOrder}
        expandedRows={expandedRows}
        processingIds={processingIds}
        onSort={handleSort}
        onToggleExpand={toggleRowExpanded}
        onApprove={handleApprove}
        onDisapprove={handleDisapprove}
        onUndo={handleUndo}
      />
    </Box>
  );
};

AIEditsTab.propTypes = {
  similarSentences: PropTypes.arrayOf(
    PropTypes.shape({
      tuid: PropTypes.string.isRequired,
      value: PropTypes.string,
      original_text: PropTypes.string,
      edited_value: PropTypes.string,
      edited_text: PropTypes.string,
      score: PropTypes.number,
      sections: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
      section: PropTypes.string,
      status: PropTypes.oneOf(['pending', 'approved', 'disapproved']),
    })
  ),
  onApproveSentence: PropTypes.func,
  onDisapproveSentence: PropTypes.func,
  onUndoDecision: PropTypes.func,
  isLoading: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
};

export default AIEditsTab;
